import JSZip from 'jszip';

import type { SheetPeek, WorkbookPeek } from './classifyFiles';

/**
 * The sheet names and first rows of an `.xlsx`, without reading the rest.
 *
 * The real Results Workbook is 94 MB on disk and its sheets inflate to a
 * quarter of a gigabyte each, so a whole-workbook parser stalls the page for
 * many seconds on drop. An `.xlsx` is a zip, so instead: the central directory
 * names the parts, `xl/workbook.xml` and its rels name the sheets, and each
 * sheet's XML is inflated chunk by chunk only until its first rows have gone
 * by. Shared strings are read in full, and only when a peeked cell points at
 * one (Alteryx writes inline strings; Excel writes shared ones).
 *
 * Text only, enough to recognise headers: numbers come back as written and
 * formulas as their cached value.
 */

/** Rows read per sheet by default: the results header is on row 1, the sales header on row 2. */
const PEEK_ROW_COUNT = 3;

const WORKBOOK_PART = 'xl/workbook.xml';
const WORKBOOK_RELS_PART = 'xl/_rels/workbook.xml.rels';
export const SHARED_STRINGS_PART = 'xl/sharedStrings.xml';

export function attribute(tag: string, name: string): string | null {
  const match = new RegExp(`\\s${name}="([^"]*)"`).exec(tag);
  return match?.[1] ?? null;
}

const ENTITIES: Record<string, string> = {
  lt: '<',
  gt: '>',
  amp: '&',
  quot: '"',
  apos: "'",
};

function decodeXml(text: string): string {
  return text.replace(
    /&(#x[0-9a-fA-F]+|#\d+|[a-z]+);/g,
    (whole, entity: string) => {
      if (entity.startsWith('#x')) {
        return String.fromCodePoint(Number.parseInt(entity.slice(2), 16));
      }
      if (entity.startsWith('#')) {
        return String.fromCodePoint(Number.parseInt(entity.slice(1), 10));
      }
      return ENTITIES[entity] ?? whole;
    },
  );
}

/** The text of every `<t>` in a run of rich text, joined. */
function textRuns(xml: string): string {
  let text = '';
  for (const match of xml.matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>/g)) {
    text += decodeXml(match[1] ?? '');
  }
  return text;
}

export function parseSharedStrings(xml: string): string[] {
  return [...xml.matchAll(/<si>([\s\S]*?)<\/si>/g)].map((match) =>
    textRuns(match[1] ?? ''),
  );
}

/** Excel's last column is XFD; a reference past it is not a cell this page will place. */
const MAX_COLUMN_INDEX = 16_383;

/** "A" → 0, "AB" → 27; null past XFD, so a crafted `r="ZZZZZZ1"` cannot ask for millions of blanks. */
export function columnIndex(reference: string): number | null {
  const letters = /^[A-Z]{1,3}/.exec(reference)?.[0];
  if (!letters || /^[A-Z]{4}/.test(reference)) return null;
  let index = 0;
  for (const letter of letters) {
    index = index * 26 + (letter.charCodeAt(0) - 64);
  }
  return index - 1 > MAX_COLUMN_INDEX ? null : index - 1;
}

/** `(Id, Target)` of each relationship, the target made relative to the zip root. */
function sheetPartsByRelationship(relsXml: string): Map<string, string> {
  const parts = new Map<string, string>();
  for (const [tag] of relsXml.matchAll(/<Relationship\b[^>]*\/?>/g)) {
    const id = attribute(tag, 'Id');
    const target = attribute(tag, 'Target');
    if (!id || !target) continue;
    const path = target.startsWith('/') ? target.slice(1) : `xl/${target}`;
    parts.set(id, path);
  }
  return parts;
}

/** The sheets in workbook order, each with the zip path of its XML. */
function sheetEntries(
  workbookXml: string,
  relsXml: string,
): { name: string; path: string }[] {
  const parts = sheetPartsByRelationship(relsXml);
  const sheets: { name: string; path: string }[] = [];
  for (const [tag] of workbookXml.matchAll(/<sheet\b[^>]*\/?>/g)) {
    const name = attribute(tag, 'name');
    const id = attribute(tag, 'r:id') ?? attribute(tag, 'id');
    const path = id ? parts.get(id) : undefined;
    if (name !== null && path) sheets.push({ name: decodeXml(name), path });
  }
  return sheets;
}

/** Complete `<row>` elements seen so far, including empty self-closing ones. */
function completeRowCount(xml: string): number {
  return xml.match(/<row\b[^>]*\/>|<\/row>/g)?.length ?? 0;
}

function sheetHeadIsComplete(xml: string, rowCount: number): boolean {
  return (
    completeRowCount(xml) >= rowCount ||
    xml.includes('</sheetData>') ||
    xml.includes('<sheetData/>')
  );
}

/**
 * jszip's chunked reader of one entry. Documented in jszip's API and shipped
 * in every 3.x build, but absent from its type definitions, which only know
 * the whole-entry `async`.
 */
interface StreamingZipObject extends JSZip.JSZipObject {
  internalStream(type: 'string'): JSZip.JSZipStreamHelper<string>;
}

export function canStream(
  entry: JSZip.JSZipObject,
): entry is StreamingZipObject {
  return (
    'internalStream' in entry && typeof entry.internalStream === 'function'
  );
}

/**
 * Inflates the entry chunk by chunk and stops as soon as `enough` says so,
 * so a sheet of a few hundred megabytes costs its first chunks.
 */
function readHead(
  entry: JSZip.JSZipObject,
  enough: (xml: string) => boolean,
): Promise<string> {
  if (!canStream(entry)) return entry.async('string');
  return new Promise((resolve, reject) => {
    let xml = '';
    let settled = false;
    const stream = entry.internalStream('string');
    stream
      .on('data', (chunk: string) => {
        if (settled) return;
        xml += chunk;
        if (enough(xml)) {
          settled = true;
          stream.pause();
          resolve(xml);
        }
      })
      .on('end', () => {
        if (settled) return;
        settled = true;
        resolve(xml);
      })
      .on('error', (error: Error) => {
        if (settled) return;
        settled = true;
        reject(error);
      })
      .resume();
  });
}

type CellReader = (
  attributes: string,
  inner: string,
) => Promise<string> | string;

/**
 * The column a cell's `r` attribute names, the next one when it has none, or
 * null for a reference no real sheet has (past XFD), which is left out.
 */
function cellColumn(attributes: string, next: number): number | null {
  const reference = attribute(attributes, 'r');
  if (reference === null) return next;
  return columnIndex(reference);
}

/** One `<row>`'s cells as text by column, blanks for columns no cell names. */
async function parseRow(
  rowXml: string,
  readCell: CellReader,
): Promise<string[]> {
  const row: string[] = [];
  for (const cell of rowXml.matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
    const attributes = cell[1] ?? '';
    const column = cellColumn(attributes, row.length);
    if (column === null) continue;
    while (row.length < column) row.push('');
    row[column] = await readCell(attributes, cell[2] ?? '');
  }
  return row;
}

/** The first `rowCount` rows of a sheet's XML. */
async function parseRows(
  xml: string,
  readCell: CellReader,
  rowCount: number,
): Promise<string[][]> {
  const rows: string[][] = [];
  for (const match of xml.matchAll(/<row\b[^>]*?(?:\/>|>([\s\S]*?)<\/row>)/g)) {
    if (rows.length >= rowCount) break;
    rows.push(await parseRow(match[1] ?? '', readCell));
  }
  return rows;
}

function isSharedString(attributes: string): boolean {
  return attribute(attributes, 't') === 's';
}

/** A cell's text: its inline string, its value, or the shared string it points at. */
export function cellText(
  attributes: string,
  inner: string,
  sharedStrings: readonly string[],
): string {
  const type = attribute(attributes, 't');
  if (type === 'inlineStr') return textRuns(inner);
  const value = /<v>([\s\S]*?)<\/v>/.exec(inner)?.[1];
  if (value === undefined) return '';
  if (type === 's') return sharedStrings[Number.parseInt(value, 10)] ?? '';
  return decodeXml(value);
}

function cellReader(sharedStrings: () => Promise<string[]>): CellReader {
  return async (attributes, inner) =>
    cellText(
      attributes,
      inner,
      isSharedString(attributes) ? await sharedStrings() : [],
    );
}

export interface OpenedWorkbook {
  zip: JSZip;
  /** The sheets in workbook order, each with the zip path of its XML. */
  sheets: { name: string; path: string }[];
}

/** The workbook's zip and its sheets; throws for a file that is not an `.xlsx`. */
export async function openWorkbook(file: Blob): Promise<OpenedWorkbook> {
  const zip = await JSZip.loadAsync(await file.arrayBuffer());
  const workbookXml = await zip.file(WORKBOOK_PART)?.async('string');
  const relsXml = await zip.file(WORKBOOK_RELS_PART)?.async('string');
  if (workbookXml === undefined || relsXml === undefined) {
    throw new Error('Not an Excel workbook (.xlsx).');
  }
  return { zip, sheets: sheetEntries(workbookXml, relsXml) };
}

export async function peekWorkbook(
  file: Blob,
  /** Rows read per sheet; the Mass Renewal looks further down for a header under a marker row. */
  rowCount: number = PEEK_ROW_COUNT,
): Promise<WorkbookPeek> {
  const { zip, sheets: entries } = await openWorkbook(file);
  // Loaded once, and only if a peeked cell is a shared string.
  let sharedStrings: Promise<string[]> | null = null;
  const readCell = cellReader(() => {
    sharedStrings ??=
      zip.file(SHARED_STRINGS_PART)?.async('string').then(parseSharedStrings) ??
      Promise.resolve([]);
    return sharedStrings;
  });
  const sheets: SheetPeek[] = [];
  for (const { name, path } of entries) {
    const entry = zip.file(path);
    if (!entry) continue;
    const head = await readHead(entry, (xml) =>
      sheetHeadIsComplete(xml, rowCount),
    );
    sheets.push({ name, rows: await parseRows(head, readCell, rowCount) });
  }
  return { sheets };
}
